<template>
  <div class="dashboard-wrapper">

    <!-- =====================================================
         HEADER
    ====================================================== -->
    <div class="dashboard-header">

      <div class="header-title">
        <div class="eyebrow">
          <span class="eyebrow-dot"></span>
          CreatorDost Admin
        </div>

        <h2>Dashboard</h2>

        <p>
          Overview of your CreatorDost platform performance.
        </p>
      </div>

      <div class="header-actions">

        <div class="period-wrapper">
          <i class="bx bx-calendar"></i>

          <select
            v-model="selectedPeriod"
            class="period-select"
          >
            <option value="all">All Time</option>
            <option value="month">This Month</option>
            <option value="today">Today</option>
          </select>
        </div>

        <button
          class="refresh-btn"
          :disabled="loading"
          @click="refreshDashboard"
        >
          <i
            class="bx bx-refresh"
            :class="{ rotating: loading }"
          ></i>

          {{ loading ? "Loading..." : "Refresh" }}
        </button>

      </div>

    </div>


    <!-- =====================================================
         LOADING
    ====================================================== -->

    <div
      v-if="loading"
      class="loading-line"
    >
      <span></span>
    </div>


    <!-- =====================================================
         PRIMARY KPI
    ====================================================== -->

    <div class="stats-grid primary-stats">

      <!-- USERS -->
      <div class="stat-card blue">

        <div class="stat-icon">
          <i class="bx bx-group"></i>
        </div>

        <div class="stat-content">
          <span>Total Users</span>

          <strong>
            {{ formatNumber(currentStats.users) }}
          </strong>

          <small>
            Registered users
          </small>
        </div>

      </div>


      <!-- COURSES -->
      <div class="stat-card purple">

        <div class="stat-icon">
          <i class="bx bx-book-open"></i>
        </div>

        <div class="stat-content">
          <span>Total Courses</span>

          <strong>
            {{ formatNumber(summary.courses) }}
          </strong>

          <small>
            Available courses
          </small>
        </div>

      </div>


      <!-- ORDERS -->
      <div class="stat-card orange">

        <div class="stat-icon">
          <i class="bx bx-cart"></i>
        </div>

        <div class="stat-content">
          <span>Total Orders</span>

          <strong>
            {{ formatNumber(currentStats.orders) }}
          </strong>

          <small>
            All purchases
          </small>
        </div>

      </div>


      <!-- REVENUE -->
      <div class="stat-card green">

        <div class="stat-icon">
          <i class="bx bx-rupee"></i>
        </div>

        <div class="stat-content">
          <span>Total Revenue</span>

          <strong>
            ₹{{ formatCompactMoney(currentStats.earnings) }}
          </strong>

          <small>
            Completed sales
          </small>
        </div>

      </div>

    </div>


    <!-- =====================================================
         SECONDARY KPI
    ====================================================== -->

    <div class="stats-grid secondary-stats">

      <div class="mini-stat">

        <div class="mini-icon blue">
          <i class="bx bx-line-chart"></i>
        </div>

        <div>
          <span>Learning Activity</span>

          <strong>
            {{ formatNumber(summary.learningProgress) }}
          </strong>

          <small>
            Active learning
          </small>
        </div>

      </div>


      <div class="mini-stat">

        <div class="mini-icon green">
          <i class="bx bx-check-circle"></i>
        </div>

        <div>
          <span>Completed Courses</span>

          <strong>
            {{ formatNumber(summary.completedProgress) }}
          </strong>

          <small>
            Learning completed
          </small>
        </div>

      </div>


      <div class="mini-stat">

        <div class="mini-icon purple">
          <i class="bx bx-user-voice"></i>
        </div>

        <div>
          <span>Mentors</span>

          <strong>
            {{ formatNumber(summary.mentors) }}
          </strong>

          <small>
            Creator mentors
          </small>
        </div>

      </div>


      <div class="mini-stat">

        <div class="mini-icon yellow">
          <i class="bx bx-star"></i>
        </div>

        <div>
          <span>Reviews</span>

          <strong>
            {{ formatNumber(summary.reviews) }}
          </strong>

          <small>
            ★ {{ Number(summary.averageRating || 0).toFixed(1) }}
            average
          </small>
        </div>

      </div>

    </div>


    <!-- =====================================================
         ROW 1
         AREA + DONUT
    ====================================================== -->

    <div class="charts-grid revenue-row">

      <!-- REVENUE AREA -->
      <div class="dashboard-card large-card">

        <div class="card-header">

          <div>
            <h3>Revenue Trend</h3>

            <p>
              Revenue performance over time
            </p>
          </div>

          <div class="header-total">
            <span>Total Revenue</span>

            <strong>
              ₹{{ formatCompactMoney(currentStats.earnings) }}
            </strong>
          </div>

        </div>

        <div
          id="revenueChart"
          class="chart-container"
        ></div>

      </div>


      <!-- ORDER STATUS DONUT -->
      <div class="dashboard-card">

        <div class="card-header">

          <div>
            <h3>Order Status</h3>

            <p>
              Purchase distribution
            </p>
          </div>

          <div class="chart-icon green">
            <i class="bx bx-pie-chart-alt-2"></i>
          </div>

        </div>

        <div
          id="orderStatusChart"
          class="chart-container donut-container"
        ></div>

      </div>

    </div>


    <!-- =====================================================
         ROW 2
         LINE + RADIAL
    ====================================================== -->

    <div class="charts-grid">

      <!-- USER GROWTH LINE -->
      <div class="dashboard-card large-card">

        <div class="card-header">

          <div>
            <h3>User Growth</h3>

            <p>
              Users and enrolled users over time
            </p>
          </div>

          <div class="chart-icon blue">
            <i class="bx bx-trending-up"></i>
          </div>

        </div>

        <div
          id="userEnrollmentChart"
          class="chart-container"
        ></div>

      </div>


      <!-- LEARNING RADIAL -->
      <div class="dashboard-card">

        <div class="card-header">

          <div>
            <h3>Course Completion</h3>

            <p>
              Overall learning completion
            </p>
          </div>

          <div class="chart-icon purple">
            <i class="bx bx-target-lock"></i>
          </div>

        </div>

        <div
          id="learningProgressChart"
          class="chart-container radial-container"
        ></div>

        <div class="completion-summary">

          <div>
            <strong>
              {{ formatNumber(completedLearningCount) }}
            </strong>

            <span>Completed</span>
          </div>

          <div>
            <strong>
              {{ formatNumber(totalLearningCount) }}
            </strong>

            <span>Total</span>
          </div>

        </div>

      </div>

    </div>


    <!-- =====================================================
         COURSE CONTENT
    ====================================================== -->

    <div class="dashboard-card content-card">

      <div class="card-header">

        <div>
          <h3>Courses & Content</h3>

          <p>
            Platform content inventory
          </p>
        </div>

        <div class="chart-icon purple">
          <i class="bx bx-layer"></i>
        </div>

      </div>


      <div class="content-grid">

        <div
          v-for="item in contentItems"
          :key="item.key"
          class="content-item"
        >

          <div
            class="content-icon"
            :class="item.color"
          >
            <i :class="item.icon"></i>
          </div>

          <div>

            <span>
              {{ item.label }}
            </span>

            <strong>
              {{ formatNumber(summary[item.key]) }}
            </strong>

          </div>

        </div>

      </div>

    </div>


    <!-- =====================================================
         ROW 3
         HORIZONTAL BAR + COLUMN BAR
    ====================================================== -->

    <div class="charts-grid">

      <!-- TOP COURSES -->
      <div class="dashboard-card large-card">

        <div class="card-header">

          <div>
            <h3>Top Courses</h3>

            <p>
              Courses ranked by completed orders
            </p>
          </div>

          <span class="chart-badge">
            Top {{ topCoursesData.labels.length }}
          </span>

        </div>

        <div
          id="topCoursesChart"
          class="chart-container"
        ></div>

      </div>


      <!-- CATEGORIES COLUMN -->
      <div class="dashboard-card">

        <div class="card-header">

          <div>
            <h3>Category Performance</h3>

            <p>
              Course distribution by category
            </p>
          </div>

          <div class="chart-icon orange">
            <i class="bx bx-category"></i>
          </div>

        </div>

        <div
          id="categoryChart"
          class="chart-container"
        ></div>

      </div>

    </div>


    <!-- =====================================================
         ROW 4
         MENTORS + REVIEWS
    ====================================================== -->

    <div class="charts-grid">

      <!-- MENTOR BAR -->
      <div class="dashboard-card">

        <div class="card-header">

          <div>
            <h3>Top Mentors</h3>

            <p>
              Mentors ranked by sales
            </p>
          </div>

          <div class="chart-icon purple">
            <i class="bx bx-user-voice"></i>
          </div>

        </div>

        <div
          id="mentorChart"
          class="chart-container"
        ></div>

      </div>


      <!-- REVIEW BAR -->
      <div class="dashboard-card">

        <div class="card-header">

          <div>
            <h3>Ratings & Reviews</h3>

            <p>
              {{ formatNumber(summary.reviews) }}
              total reviews
            </p>
          </div>

          <div class="rating-box">

            <i class="bx bxs-star"></i>

            <strong>
              {{ Number(summary.averageRating || 0).toFixed(1) }}
            </strong>

          </div>

        </div>

        <div
          id="reviewChart"
          class="chart-container"
        ></div>

      </div>

    </div>


    <!-- =====================================================
         REPORT CARDS
    ====================================================== -->

    <div class="report-grid">

      <!-- STORIES -->
      <div class="dashboard-card report-card">

        <div class="report-top">

          <div class="report-icon yellow">
            <i class="bx bx-trophy"></i>
          </div>

          <div>
            <h3>Success Stories</h3>

            <p>
              Creator results
            </p>
          </div>

        </div>

        <strong class="report-number">
          {{ formatNumber(summary.stories) }}
        </strong>

        <div class="report-footer">
          <span>Total stories</span>
          <i class="bx bx-right-arrow-alt"></i>
        </div>

      </div>


      <!-- BLOGS -->
      <div class="dashboard-card report-card">

        <div class="report-top">

          <div class="report-icon purple">
            <i class="bx bx-edit"></i>
          </div>

          <div>
            <h3>Blogs</h3>

            <p>
              Educational content
            </p>
          </div>

        </div>

        <strong class="report-number">
          {{ formatNumber(summary.blogs) }}
        </strong>

        <div class="report-footer">
          <span>Total articles</span>
          <i class="bx bx-right-arrow-alt"></i>
        </div>

      </div>


      <!-- CATEGORIES -->
      <div class="dashboard-card report-card">

        <div class="report-top">

          <div class="report-icon cyan">
            <i class="bx bx-category-alt"></i>
          </div>

          <div>
            <h3>Categories</h3>

            <p>
              Course organization
            </p>
          </div>

        </div>

        <strong class="report-number">
          {{ formatNumber(summary.categories) }}
        </strong>

        <div class="report-footer">
          <span>Total categories</span>
          <i class="bx bx-right-arrow-alt"></i>
        </div>

      </div>

    </div>


    <!-- =====================================================
         RECENT ACTIVITY
    ====================================================== -->

    <div class="dashboard-card activity-card">

      <div class="card-header">

        <div>
          <h3>Recent Activity</h3>

          <p>
            Latest platform events
          </p>
        </div>

        <div class="chart-icon blue">
          <i class="bx bx-history"></i>
        </div>

      </div>


      <div
        v-if="recentActivity.length"
        class="activity-list"
      >

        <div
          v-for="(activity, index) in recentActivity"
          :key="
            activity.createdAt +
            activity.label +
            index
          "
          class="activity-row"
        >

          <div class="activity-icon">
            <i class="bx bx-pulse"></i>
          </div>

          <div class="activity-content">

            <strong>
              {{ activity.label }}
            </strong>

            <span>
              {{ relativeTime(activity.createdAt) }}
            </span>

          </div>

          <i class="bx bx-chevron-right"></i>

        </div>

      </div>


      <div
        v-else
        class="empty-state"
      >

        <i class="bx bx-time-five"></i>

        <span>
          No recent activity
        </span>

      </div>

    </div>

  </div>
</template>


<script>
import ApexCharts from "apexcharts";
import { getDashboardApi } from "@/apis/dashboardapis";

export default {
  name: "DashboardScreen",

  data() {
    return {

      selectedPeriod: "all",

      loading: false,

      charts: {
        revenue: null,
        orderStatus: null,
        userEnrollment: null,
        learningProgress: null,
        topCourses: null,
        category: null,
        mentor: null,
        review: null,
      },

      currentStatsData: {
        users: 0,
        enrolled: 0,
        earnings: 0,
        orders: 0,
      },

      summary: {
        courses: 0,
        mentors: 0,
        modules: 0,
        lectures: 0,
        products: 0,
        faqs: 0,
        orders: 0,
        learningProgress: 0,
        completedProgress: 0,
        users: 0,
        reviews: 0,
        averageRating: 0,
        stories: 0,
        blogs: 0,
        categories: 0,
      },

      userEnrollmentData: {
        categories: [],
        users: [],
        enrolled: [],
      },

      revenueData: {
        categories: [],
        values: [],
      },

      orderStatusData: {
        labels: [],
        values: [],
      },

      learningStatusData: {
        labels: [],
        values: [],
      },

      categoryData: {
        labels: [],
        values: [],
      },

      topCoursesData: {
        labels: [],
        values: [],
      },

      topMentorsData: {
        labels: [],
        values: [],
      },

      reviewData: {
        labels: [],
        values: [],
      },

      recentActivity: [],
    };
  },


  computed: {

    currentStats() {
      return this.currentStatsData;
    },


    contentItems() {
      return [
        {
          key: "courses",
          label: "Courses",
          icon: "bx bx-book-open",
          color: "blue",
        },

        {
          key: "modules",
          label: "Modules",
          icon: "bx bx-layer",
          color: "purple",
        },

        {
          key: "lectures",
          label: "Lectures",
          icon: "bx bx-video",
          color: "green",
        },

        {
          key: "products",
          label: "Products",
          icon: "bx bx-briefcase",
          color: "orange",
        },

        {
          key: "faqs",
          label: "FAQs",
          icon: "bx bx-help-circle",
          color: "red",
        },

        {
          key: "categories",
          label: "Categories",
          icon: "bx bx-category",
          color: "cyan",
        },
      ];
    },


    completedLearningCount() {

      const labels =
        this.learningStatusData.labels || [];

      const values =
        this.learningStatusData.values || [];

      const index =
        labels.findIndex(
          (label) =>
            String(label)
              .toLowerCase()
              .includes("complete")
        );

      if (index === -1) {
        return Number(
          this.summary.completedProgress || 0
        );
      }

      return Number(values[index] || 0);
    },


    totalLearningCount() {

      const values =
        this.learningStatusData.values || [];

      if (!values.length) {
        return (
          Number(this.summary.completedProgress || 0) +
          Number(
            this.summary.learningProgress || 0
          )
        );
      }

      return values.reduce(
        (sum, value) =>
          sum + Number(value || 0),
        0
      );
    },


    completionPercentage() {

      if (!this.totalLearningCount) {
        return 0;
      }

      return Math.round(
        (
          this.completedLearningCount /
          this.totalLearningCount
        ) * 100
      );
    },

  },


  async mounted() {
    await this.loadDashboard();
  },


  beforeUnmount() {
    this.destroyCharts();
  },


  methods: {

    /* =====================================================
       LOAD DASHBOARD
    ====================================================== */

    async loadDashboard() {

      this.loading = true;

      try {

        const response =
          await getDashboardApi({
            period: this.selectedPeriod,
          });


        if (
          response &&
          response.success === true &&
          response.data
        ) {

          const data = response.data;


          this.currentStatsData = {
            users:
              Number(
                data.stats?.users || 0
              ),

            enrolled:
              Number(
                data.stats?.enrolled || 0
              ),

            earnings:
              Number(
                data.stats?.earnings || 0
              ),

            orders:
              Number(
                data.stats?.orders || 0
              ),
          };


          this.summary = {

            courses:
              Number(
                data.summary?.courses || 0
              ),

            mentors:
              Number(
                data.summary?.mentors || 0
              ),

            modules:
              Number(
                data.summary?.modules || 0
              ),

            lectures:
              Number(
                data.summary?.lectures || 0
              ),

            products:
              Number(
                data.summary?.products || 0
              ),

            faqs:
              Number(
                data.summary?.faqs || 0
              ),

            orders:
              Number(
                data.summary?.orders || 0
              ),

            learningProgress:
              Number(
                data.summary?.learningProgress || 0
              ),

            completedProgress:
              Number(
                data.summary?.completedProgress || 0
              ),

            users:
              Number(
                data.summary?.users || 0
              ),

            reviews:
              Number(
                data.summary?.reviews || 0
              ),

            averageRating:
              Number(
                data.summary?.averageRating || 0
              ),

            stories:
              Number(
                data.summary?.stories || 0
              ),

            blogs:
              Number(
                data.summary?.blogs || 0
              ),

            categories:
              Number(
                data.summary?.categories || 0
              ),
          };


          this.userEnrollmentData = {

            categories:
              data.userEnrollment?.categories || [],

            users:
              data.userEnrollment?.users || [],

            enrolled:
              data.userEnrollment?.enrolled || [],
          };


          this.revenueData = {

            categories:
              data.revenue?.categories || [],

            values:
              data.revenue?.values || [],
          };


          this.orderStatusData =
            data.orderStatus || {
              labels: [],
              values: [],
            };


          this.learningStatusData =
            data.learningStatus || {
              labels: [],
              values: [],
            };


          this.categoryData =
            data.categoryDistribution || {
              labels: [],
              values: [],
            };


          this.topCoursesData =
            data.topCourses || {
              labels: [],
              values: [],
            };


          this.topMentorsData =
            data.topMentors || {
              labels: [],
              values: [],
            };


          this.reviewData =
            data.reviewDistribution || {
              labels: [],
              values: [],
            };


          this.recentActivity =
            data.recentActivity || [];


          this.destroyCharts();

          await this.$nextTick();

          this.initCharts();
        }

      } catch (error) {

        console.error(
          "Dashboard loading error:",
          error
        );

      } finally {

        this.loading = false;

      }
    },


    /* =====================================================
       NUMBER FORMAT
    ====================================================== */

    formatNumber(value) {

      return new Intl.NumberFormat(
        "en-IN"
      ).format(
        Number(value || 0)
      );

    },


    /* =====================================================
       MONEY FORMAT
    ====================================================== */

    formatMoney(value) {

      return new Intl.NumberFormat(
        "en-IN",
        {
          maximumFractionDigits: 0,
        }
      ).format(
        Number(value || 0)
      );

    },


    /* =====================================================
       COMPACT MONEY
    ====================================================== */

    formatCompactMoney(value) {

      value =
        Number(value || 0);


      if (value >= 10000000) {

        return (
          (value / 10000000)
            .toFixed(1) +
          "Cr"
        );

      }


      if (value >= 100000) {

        return (
          (value / 100000)
            .toFixed(1) +
          "L"
        );

      }


      if (value >= 1000) {

        return (
          (value / 1000)
            .toFixed(1) +
          "K"
        );

      }


      return Math.round(value);

    },


    /* =====================================================
       REFRESH
    ====================================================== */

    async refreshDashboard() {

      await this.loadDashboard();

    },


    /* =====================================================
       BASE CHART
    ====================================================== */

    baseChart() {

      return {

        toolbar: {
          show: false,
        },

        zoom: {
          enabled: false,
        },

        fontFamily: "inherit",

        foreColor: "#64748B",

        animations: {
          enabled: true,

          easing: "easeinout",

          speed: 650,
        },

        parentHeightOffset: 0,

      };

    },


    /* =====================================================
       INIT ALL
    ====================================================== */

    initCharts() {

      this.initRevenueChart();

      this.initOrderStatusChart();

      this.initUserEnrollmentChart();

      this.initLearningProgressChart();

      this.initTopCoursesChart();

      this.initCategoryChart();

      this.initMentorChart();

      this.initReviewChart();

    },


    /* =====================================================
       REVENUE AREA CHART
    ====================================================== */

    initRevenueChart() {

      const element =
        document.querySelector(
          "#revenueChart"
        );


      if (!element) {
        return;
      }


      const data =
        this.revenueData;


      if (
        !data.values.length ||
        data.values.every(
          (value) =>
            Number(value || 0) === 0
        )
      ) {

        this.renderEmptyChart(
          element,
          "No revenue data available"
        );

        return;
      }


      this.charts.revenue =
        new ApexCharts(
          element,
          {

            chart: {
              ...this.baseChart(),

              type: "area",

              height: 340,
            },


            series: [
              {
                name: "Revenue",

                data:
                  data.values.map(
                    (value) =>
                      Number(value || 0)
                  ),
              },
            ],


            colors: [
              "#2563EB"
            ],


            stroke: {

              curve: "smooth",

              width: 3,

            },


            fill: {

              type: "gradient",

              gradient: {

                shadeIntensity: 1,

                opacityFrom: 0.28,

                opacityTo: 0.02,

                stops: [
                  0,
                  100
                ],

              },

            },


            markers: {

              size: 0,

              hover: {
                size: 6,
              },

            },


            xaxis: {

              categories:
                data.categories,

              labels: {

                rotate: 0,

                hideOverlappingLabels:
                  true,

                style: {

                  colors:
                    "#94A3B8",

                  fontSize:
                    "11px",

                },

              },

              axisBorder: {
                show: false,
              },

              axisTicks: {
                show: false,
              },

            },


            yaxis: {

              labels: {

                formatter:
                  (value) =>
                    "₹" +
                    this.formatCompactMoney(
                      value
                    ),

                style: {

                  colors:
                    "#94A3B8",

                  fontSize:
                    "11px",

                },

              },

            },


            grid: {

              borderColor:
                "#EEF2F7",

              strokeDashArray:
                4,

            },


            dataLabels: {
              enabled: false,
            },


            tooltip: {

              shared: true,

              intersect: false,

              theme: "light",

              y: {

                formatter:
                  (value) =>
                    "₹" +
                    this.formatMoney(
                      value
                    ),

              },

            },

          }
        );


      this.charts.revenue.render();

    },


    /* =====================================================
       ORDER STATUS DONUT
    ====================================================== */

    initOrderStatusChart() {

      this.initDonutChart(
        "#orderStatusChart",
        "orderStatus",
        this.orderStatusData,
        [
          "#10B981",
          "#F59E0B",
          "#EF4444",
          "#64748B",
          "#8B5CF6",
        ],
        "Orders"
      );

    },


    /* =====================================================
       GENERIC DONUT
    ====================================================== */

    initDonutChart(
      selector,
      key,
      data,
      colors,
      centerLabel
    ) {

      const element =
        document.querySelector(
          selector
        );


      if (!element) {
        return;
      }


      const values =
        (data.values || [])
          .map(
            (value) =>
              Number(value || 0)
          );


      const labels =
        data.labels || [];


      if (
        !values.length ||
        values.every(
          (value) =>
            value === 0
        )
      ) {

        this.renderEmptyChart(
          element,
          "No data available"
        );

        return;
      }


      const total =
        values.reduce(
          (sum, value) =>
            sum + value,
          0
        );


      this.charts[key] =
        new ApexCharts(
          element,
          {

            chart: {

              ...this.baseChart(),

              type: "donut",

              height: 310,

            },


            labels,

            series: values,

            colors,


            stroke: {

              width: 4,

              colors: [
                "#FFFFFF"
              ],

            },


            dataLabels: {
              enabled: false,
            },


            plotOptions: {

              pie: {

                expandOnClick:
                  true,

                donut: {

                  size: "72%",

                  labels: {

                    show: true,

                    name: {

                      show: true,

                      color:
                        "#64748B",

                      fontSize:
                        "11px",

                      fontWeight:
                        600,

                    },

                    value: {

                      show: true,

                      color:
                        "#0F172A",

                      fontSize:
                        "24px",

                      fontWeight:
                        800,

                      formatter:
                        (value) =>
                          this.formatNumber(
                            value
                          ),

                    },

                    total: {

                      show: true,

                      label:
                        centerLabel,

                      color:
                        "#94A3B8",

                      formatter:
                        () =>
                          this.formatNumber(
                            total
                          ),

                    },

                  },

                },

              },

            },


            legend: {

              position: "bottom",

              horizontalAlign:
                "center",

              fontSize: "10px",

              fontWeight: 600,

              labels: {
                colors:
                  "#475569",
              },

              markers: {

                width: 8,

                height: 8,

                radius: 4,

              },

              itemMargin: {

                horizontal: 7,

                vertical: 3,

              },

            },


            tooltip: {

              theme: "light",

              y: {

                formatter:
                  (value) =>
                    this.formatNumber(
                      value
                    ),

              },

            },

          }
        );


      this.charts[key].render();

    },


    /* =====================================================
       USER GROWTH LINE
    ====================================================== */

    initUserEnrollmentChart() {

      const element =
        document.querySelector(
          "#userEnrollmentChart"
        );


      if (!element) {
        return;
      }


      const data =
        this.userEnrollmentData;


      if (
        !data.categories.length
      ) {

        this.renderEmptyChart(
          element,
          "No user growth data available"
        );

        return;
      }


      this.charts.userEnrollment =
        new ApexCharts(
          element,
          {

            chart: {

              ...this.baseChart(),

              type: "line",

              height: 340,

            },


            series: [

              {
                name: "Users",

                data:
                  data.users.map(
                    (value) =>
                      Number(value || 0)
                  ),
              },

              {
                name: "Enrolled Users",

                data:
                  data.enrolled.map(
                    (value) =>
                      Number(value || 0)
                  ),
              },

            ],


            colors: [
              "#2563EB",
              "#8B5CF6"
            ],


            stroke: {

              curve: "smooth",

              width: 3,

            },


            markers: {

              size: 3,

              strokeWidth: 2,

              strokeColors:
                "#FFFFFF",

              hover: {
                size: 6,
              },

            },


            xaxis: {

              categories:
                data.categories,

              labels: {

                style: {

                  colors:
                    "#94A3B8",

                  fontSize:
                    "11px",

                },

              },

              axisBorder: {
                show: false,
              },

              axisTicks: {
                show: false,
              },

            },


            yaxis: {

              labels: {

                formatter:
                  (value) =>
                    this.formatNumber(
                      value
                    ),

                style: {

                  colors:
                    "#94A3B8",

                  fontSize:
                    "11px",

                },

              },

            },


            grid: {

              borderColor:
                "#EEF2F7",

              strokeDashArray:
                4,

            },


            dataLabels: {
              enabled: false,
            },


            legend: {

              position: "top",

              horizontalAlign:
                "right",

              fontSize: "10px",

              fontWeight: 600,

            },


            tooltip: {

              shared: true,

              intersect: false,

              theme: "light",

              y: {

                formatter:
                  (value) =>
                    this.formatNumber(
                      value
                    ),

              },

            },

          }
        );


      this.charts.userEnrollment.render();

    },


    /* =====================================================
       LEARNING RADIAL BAR
    ====================================================== */

    initLearningProgressChart() {

      const element =
        document.querySelector(
          "#learningProgressChart"
        );


      if (!element) {
        return;
      }


      const percentage =
        this.completionPercentage;


      this.charts.learningProgress =
        new ApexCharts(
          element,
          {

            chart: {

              ...this.baseChart(),

              type: "radialBar",

              height: 285,

            },


            series: [
              percentage
            ],


            colors: [
              "#10B981"
            ],


            plotOptions: {

              radialBar: {

                startAngle: -135,

                endAngle: 135,

                hollow: {

                  size: "68%",

                },

                track: {

                  background:
                    "#E8EEF5",

                  strokeWidth:
                    "100%",

                },

                dataLabels: {

                  name: {

                    show: true,

                    color:
                      "#64748B",

                    fontSize:
                      "11px",

                    fontWeight:
                      600,

                    offsetY:
                      25,

                  },

                  value: {

                    show: true,

                    color:
                      "#0F172A",

                    fontSize:
                      "31px",

                    fontWeight:
                      800,

                    offsetY:
                      -12,

                    formatter:
                      (value) =>
                        `${Math.round(
                          value
                        )}%`,

                  },

                },

              },

            },


            stroke: {

              lineCap:
                "round",

            },


            labels: [
              "Completion Rate"
            ],


            tooltip: {

              enabled: false,

            },

          }
        );


      this.charts.learningProgress.render();

    },


    /* =====================================================
       TOP COURSES HORIZONTAL BAR
    ====================================================== */

    initTopCoursesChart() {

      const element =
        document.querySelector(
          "#topCoursesChart"
        );


      if (!element) {
        return;
      }


      const labels =
        this.topCoursesData.labels || [];


      const values =
        (
          this.topCoursesData.values ||
          []
        ).map(
          (value) =>
            Number(value || 0)
        );


      if (
        !labels.length ||
        !values.length
      ) {

        this.renderEmptyChart(
          element,
          "No course performance data"
        );

        return;
      }


      this.charts.topCourses =
        new ApexCharts(
          element,
          {

            chart: {

              ...this.baseChart(),

              type: "bar",

              height:
                Math.max(
                  290,
                  labels.length * 45
                ),

            },


            series: [

              {
                name: "Orders",

                data: values,

              },

            ],


            colors: [
              "#2563EB"
            ],


            plotOptions: {

              bar: {

                horizontal: true,

                borderRadius: 5,

                barHeight: "52%",

                distributed: false,

              },

            },


            dataLabels: {

              enabled: true,

              textAnchor: "start",

              offsetX: 5,

              style: {

                colors: [
                  "#334155"
                ],

                fontSize:
                  "10px",

                fontWeight:
                  700,

              },

              formatter:
                (value) =>
                  this.formatNumber(
                    value
                  ),

            },


            xaxis: {

              categories:
                labels,

              labels: {

                formatter:
                  (value) =>
                    this.formatNumber(
                      value
                    ),

                style: {

                  colors:
                    "#94A3B8",

                  fontSize:
                    "10px",

                },

              },

              axisBorder: {
                show: false,
              },

              axisTicks: {
                show: false,
              },

            },


            yaxis: {

              labels: {

                maxWidth: 150,

                style: {

                  colors:
                    "#334155",

                  fontSize:
                    "10px",

                  fontWeight:
                    600,

                },

              },

            },


            grid: {

              borderColor:
                "#EEF2F7",

              strokeDashArray:
                4,

            },


            legend: {
              show: false,
            },


            tooltip: {

              theme: "light",

              y: {

                formatter:
                  (value) =>
                    `${this.formatNumber(
                      value
                    )} orders`,

              },

            },

          }
        );


      this.charts.topCourses.render();

    },


    /* =====================================================
       CATEGORY COLUMN BAR
    ====================================================== */

    initCategoryChart() {

      const element =
        document.querySelector(
          "#categoryChart"
        );


      if (!element) {
        return;
      }


      const labels =
        this.categoryData.labels || [];


      const values =
        (
          this.categoryData.values ||
          []
        ).map(
          (value) =>
            Number(value || 0)
        );


      if (
        !labels.length ||
        !values.length
      ) {

        this.renderEmptyChart(
          element,
          "No category data available"
        );

        return;
      }


      this.charts.category =
        new ApexCharts(
          element,
          {

            chart: {

              ...this.baseChart(),

              type: "bar",

              height: 315,

            },


            series: [

              {
                name: "Courses",

                data: values,

              },

            ],


            colors: [
              "#7C3AED"
            ],


            plotOptions: {

              bar: {

                horizontal: false,

                borderRadius: 6,

                columnWidth: "52%",

                distributed: true,

              },

            },


            dataLabels: {

              enabled: true,

              offsetY: -7,

              style: {

                colors: [
                  "#334155"
                ],

                fontSize:
                  "10px",

                fontWeight:
                  700,

              },

              formatter:
                (value) =>
                  this.formatNumber(
                    value
                  ),

            },


            xaxis: {

              categories:
                labels,

              labels: {

                rotate: -35,

                trim: false,

                style: {

                  colors:
                    "#64748B",

                  fontSize:
                    "9px",

                  fontWeight:
                    600,

                },

              },

              axisBorder: {
                show: false,
              },

              axisTicks: {
                show: false,
              },

            },


            yaxis: {

              labels: {

                formatter:
                  (value) =>
                    this.formatNumber(
                      value
                    ),

                style: {

                  colors:
                    "#94A3B8",

                  fontSize:
                    "10px",

                },

              },

            },


            grid: {

              borderColor:
                "#EEF2F7",

              strokeDashArray:
                4,

            },


            legend: {
              show: false,
            },


            tooltip: {

              theme: "light",

              y: {

                formatter:
                  (value) =>
                    this.formatNumber(
                      value
                    ),

              },

            },

          }
        );


      this.charts.category.render();

    },


    /* =====================================================
       MENTOR HORIZONTAL BAR
    ====================================================== */

    initMentorChart() {

      const element =
        document.querySelector(
          "#mentorChart"
        );


      if (!element) {
        return;
      }


      const labels =
        this.topMentorsData.labels || [];


      const values =
        (
          this.topMentorsData.values ||
          []
        ).map(
          (value) =>
            Number(value || 0)
        );


      if (
        !labels.length ||
        !values.length
      ) {

        this.renderEmptyChart(
          element,
          "No mentor sales data available"
        );

        return;
      }


      this.charts.mentor =
        new ApexCharts(
          element,
          {

            chart: {

              ...this.baseChart(),

              type: "bar",

              height:
                Math.max(
                  285,
                  labels.length * 42
                ),

            },


            series: [

              {
                name: "Sales",

                data: values,

              },

            ],


            colors: [
              "#8B5CF6"
            ],


            plotOptions: {

              bar: {

                horizontal: true,

                borderRadius: 5,

                barHeight: "48%",

              },

            },


            dataLabels: {

              enabled: true,

              textAnchor: "start",

              offsetX: 6,

              style: {

                colors: [
                  "#334155"
                ],

                fontSize:
                  "10px",

                fontWeight:
                  700,

              },

              formatter:
                (value) =>
                  this.formatNumber(
                    value
                  ),

            },


            xaxis: {

              categories:
                labels,

              labels: {

                formatter:
                  (value) =>
                    this.formatNumber(
                      value
                    ),

                style: {

                  colors:
                    "#94A3B8",

                  fontSize:
                    "9px",

                },

              },

              axisBorder: {
                show: false,
              },

              axisTicks: {
                show: false,
              },

            },


            yaxis: {

              labels: {

                maxWidth: 120,

                style: {

                  colors:
                    "#334155",

                  fontSize:
                    "10px",

                  fontWeight:
                    600,

                },

              },

            },


            grid: {

              borderColor:
                "#EEF2F7",

              strokeDashArray:
                4,

            },


            legend: {
              show: false,
            },


            tooltip: {

              theme: "light",

              y: {

                formatter:
                  (value) =>
                    `${this.formatNumber(
                      value
                    )} sales`,

              },

            },

          }
        );


      this.charts.mentor.render();

    },


    /* =====================================================
       REVIEWS HORIZONTAL BAR
    ====================================================== */

    initReviewChart() {

      const element =
        document.querySelector(
          "#reviewChart"
        );


      if (!element) {
        return;
      }


      const labels =
        this.reviewData.labels || [];


      const values =
        (
          this.reviewData.values ||
          []
        ).map(
          (value) =>
            Number(value || 0)
        );


      if (
        !labels.length ||
        !values.length
      ) {

        this.renderEmptyChart(
          element,
          "No review data available"
        );

        return;
      }


      this.charts.review =
        new ApexCharts(
          element,
          {

            chart: {

              ...this.baseChart(),

              type: "bar",

              height:
                Math.max(
                  270,
                  labels.length * 44
                ),

            },


            series: [

              {
                name: "Reviews",

                data: values,

              },

            ],


            colors: [
              "#F59E0B"
            ],


            plotOptions: {

              bar: {

                horizontal: true,

                borderRadius: 5,

                barHeight: "48%",

              },

            },


            dataLabels: {

              enabled: true,

              textAnchor: "start",

              offsetX: 6,

              style: {

                colors: [
                  "#334155"
                ],

                fontSize:
                  "10px",

                fontWeight:
                  700,

              },

              formatter:
                (value) =>
                  this.formatNumber(
                    value
                  ),

            },


            xaxis: {

              categories:
                labels,

              labels: {

                formatter:
                  (value) =>
                    this.formatNumber(
                      value
                    ),

                style: {

                  colors:
                    "#94A3B8",

                  fontSize:
                    "9px",

                },

              },

              axisBorder: {
                show: false,
              },

              axisTicks: {
                show: false,
              },

            },


            yaxis: {

              labels: {

                maxWidth: 90,

                style: {

                  colors:
                    "#334155",

                  fontSize:
                    "10px",

                  fontWeight:
                    700,

                },

              },

            },


            grid: {

              borderColor:
                "#EEF2F7",

              strokeDashArray:
                4,

            },


            legend: {
              show: false,
            },


            tooltip: {

              theme: "light",

              y: {

                formatter:
                  (value) =>
                    `${this.formatNumber(
                      value
                    )} reviews`,

              },

            },

          }
        );


      this.charts.review.render();

    },


    /* =====================================================
       EMPTY CHART
    ====================================================== */

    renderEmptyChart(
      element,
      message
    ) {

      element.innerHTML = `
        <div class="chart-empty">
          <div class="chart-empty-icon">
            <i class="bx bx-bar-chart-alt-2"></i>
          </div>

          <span>
            ${message}
          </span>
        </div>
      `;

    },


    /* =====================================================
       RELATIVE TIME
    ====================================================== */

    relativeTime(value) {

      if (!value) {
        return "";
      }


      const timestamp =
        new Date(value).getTime();


      if (
        Number.isNaN(timestamp)
      ) {
        return "";
      }


      const minutes =
        Math.max(
          1,
          Math.round(
            (
              Date.now() -
              timestamp
            ) / 60000
          )
        );


      if (minutes < 60) {
        return `${minutes} min ago`;
      }


      const hours =
        Math.round(
          minutes / 60
        );


      if (hours < 24) {
        return `${hours} hr ago`;
      }


      const days =
        Math.round(
          hours / 24
        );


      return `${days} day${
        days !== 1
          ? "s"
          : ""
      } ago`;

    },


    /* =====================================================
       DESTROY
    ====================================================== */

    destroyCharts() {

      Object.keys(
        this.charts
      ).forEach(
        (key) => {

          if (
            this.charts[key]
          ) {

            try {

              this.charts[key].destroy();

            } catch (error) {

              console.warn(
                `Unable to destroy ${key} chart`,
                error
              );

            }

            this.charts[key] =
              null;

          }

        }
      );

    },

  },


  watch: {

    async selectedPeriod() {

      await this.loadDashboard();

    },

  },

};
</script>


<style scoped>

/* =========================================================
   BASE
========================================================= */

.dashboard-wrapper {
  width: 100%;
  min-height: 100vh;

  padding: 28px;

  box-sizing: border-box;

  background:
    radial-gradient(
      circle at 0% 0%,
      rgba(37, 99, 235, 0.045),
      transparent 28%
    ),
    #F8FAFC;

  color: #0F172A;
}


/* =========================================================
   HEADER
========================================================= */

.dashboard-header {
  display: flex;

  align-items: flex-end;

  justify-content: space-between;

  gap: 20px;

  margin-bottom: 22px;
}

.eyebrow {
  display: flex;

  align-items: center;

  gap: 7px;

  margin-bottom: 7px;

  color: #64748B;

  font-size: 9px;

  font-weight: 800;

  letter-spacing: 0.8px;

  text-transform: uppercase;
}

.eyebrow-dot {
  width: 7px;
  height: 7px;

  border-radius: 50%;

  background: #2563EB;

  box-shadow:
    0 0 0 4px
    rgba(37, 99, 235, 0.1);
}

.dashboard-header h2 {
  margin: 0;

  color: #0F172A;

  font-size: 27px;

  line-height: 1.1;

  font-weight: 800;

  letter-spacing: -0.5px;
}

.dashboard-header p {
  margin: 7px 0 0;

  color: #64748B;

  font-size: 12px;
}

.header-actions {
  display: flex;

  align-items: center;

  gap: 9px;
}

.period-wrapper {
  height: 40px;

  display: flex;

  align-items: center;

  gap: 7px;

  padding: 0 11px;

  border:
    1px solid #E2E8F0;

  border-radius: 10px;

  background: #FFFFFF;
}

.period-wrapper i {
  color: #64748B;

  font-size: 16px;
}

.period-select {
  height: 38px;

  min-width: 105px;

  border: 0;

  outline: none;

  background: transparent;

  color: #334155;

  font-size: 11px;

  font-weight: 700;

  cursor: pointer;
}

.refresh-btn {
  height: 40px;

  display: flex;

  align-items: center;

  gap: 7px;

  padding: 0 14px;

  border:
    1px solid #E2E8F0;

  border-radius: 10px;

  background: #FFFFFF;

  color: #334155;

  font-size: 11px;

  font-weight: 700;

  cursor: pointer;

  transition: 0.2s ease;
}

.refresh-btn:hover {
  color: #2563EB;

  border-color: #BFDBFE;

  box-shadow:
    0 6px 16px
    rgba(37, 99, 235, 0.08);
}

.refresh-btn:disabled {
  opacity: 0.7;

  cursor: not-allowed;
}

.refresh-btn i {
  font-size: 17px;
}

.rotating {
  animation:
    spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}


/* =========================================================
   LOADING
========================================================= */

.loading-line {
  height: 2px;

  overflow: hidden;

  margin-top: -9px;

  margin-bottom: 9px;

  background: #E2E8F0;
}

.loading-line span {
  display: block;

  width: 35%;

  height: 100%;

  background: #2563EB;

  animation:
    loading 1.1s
    infinite ease-in-out;
}

@keyframes loading {
  0% {
    transform:
      translateX(-120%);
  }

  100% {
    transform:
      translateX(320%);
  }
}


/* =========================================================
   STATS
========================================================= */

.stats-grid {
  display: grid;

  gap: 14px;

  margin-bottom: 14px;
}

.primary-stats {
  grid-template-columns:
    repeat(4, minmax(0, 1fr));
}

.secondary-stats {
  grid-template-columns:
    repeat(4, minmax(0, 1fr));

  margin-bottom: 20px;
}

.stat-card {
  display: flex;

  align-items: center;

  gap: 14px;

  min-width: 0;

  padding: 18px;

  border:
    1px solid #E2E8F0;

  border-radius: 15px;

  background: #FFFFFF;

  box-shadow:
    0 2px 8px
    rgba(15, 23, 42, 0.025);

  transition:
    0.2s ease;
}

.stat-card:hover {
  transform:
    translateY(-2px);

  box-shadow:
    0 10px 25px
    rgba(15, 23, 42, 0.07);
}

.stat-icon {
  width: 48px;
  height: 48px;

  min-width: 48px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 13px;

  font-size: 22px;
}

.stat-card.blue
.stat-icon {
  color: #2563EB;

  background: #EFF6FF;
}

.stat-card.purple
.stat-icon {
  color: #7C3AED;

  background: #F5F3FF;
}

.stat-card.orange
.stat-icon {
  color: #EA580C;

  background: #FFF7ED;
}

.stat-card.green
.stat-icon {
  color: #059669;

  background: #ECFDF5;
}

.stat-content {
  min-width: 0;
}

.stat-content span {
  display: block;

  color: #64748B;

  font-size: 10px;

  font-weight: 800;

  text-transform: uppercase;

  letter-spacing: 0.35px;
}

.stat-content strong {
  display: block;

  margin-top: 5px;

  color: #0F172A;

  font-size: 24px;

  line-height: 1;

  font-weight: 800;

  letter-spacing: -0.5px;
}

.stat-content small {
  display: block;

  margin-top: 5px;

  color: #94A3B8;

  font-size: 9px;
}


/* =========================================================
   MINI STATS
========================================================= */

.mini-stat {
  display: flex;

  align-items: center;

  gap: 11px;

  padding: 14px 16px;

  border:
    1px solid #E8EDF3;

  border-radius: 13px;

  background: #FFFFFF;
}

.mini-icon {
  width: 40px;
  height: 40px;

  min-width: 40px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 10px;

  font-size: 18px;
}

.mini-icon.blue {
  color: #2563EB;

  background: #EFF6FF;
}

.mini-icon.green {
  color: #059669;

  background: #ECFDF5;
}

.mini-icon.purple {
  color: #7C3AED;

  background: #F5F3FF;
}

.mini-icon.yellow {
  color: #D97706;

  background: #FFFBEB;
}

.mini-stat span {
  display: block;

  color: #64748B;

  font-size: 9px;

  font-weight: 700;
}

.mini-stat strong {
  display: block;

  margin-top: 3px;

  color: #0F172A;

  font-size: 17px;

  font-weight: 800;
}

.mini-stat small {
  display: block;

  margin-top: 2px;

  color: #94A3B8;

  font-size: 8px;
}


/* =========================================================
   CARDS
========================================================= */

.charts-grid {
  display: grid;

  grid-template-columns:
    minmax(0, 1.35fr)
    minmax(320px, 0.85fr);

  gap: 16px;

  margin-bottom: 16px;
}

.revenue-row {
  grid-template-columns:
    minmax(0, 1.65fr)
    minmax(320px, 0.75fr);
}

.dashboard-card {
  min-width: 0;

  padding: 20px;

  border:
    1px solid #E2E8F0;

  border-radius: 16px;

  background: #FFFFFF;

  box-shadow:
    0 2px 8px
    rgba(15, 23, 42, 0.025);
}

.large-card {
  min-width: 0;
}

.card-header {
  display: flex;

  align-items: flex-start;

  justify-content: space-between;

  gap: 15px;

  margin-bottom: 8px;
}

.card-header h3 {
  margin: 0;

  color: #0F172A;

  font-size: 15px;

  font-weight: 800;
}

.card-header p {
  margin: 4px 0 0;

  color: #94A3B8;

  font-size: 10px;
}

.header-total {
  text-align: right;
}

.header-total span {
  display: block;

  color: #94A3B8;

  font-size: 8px;
}

.header-total strong {
  display: block;

  margin-top: 3px;

  color: #0F172A;

  font-size: 16px;

  font-weight: 800;
}

.chart-icon {
  width: 34px;
  height: 34px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 9px;

  font-size: 16px;
}

.chart-icon.blue {
  color: #2563EB;

  background: #EFF6FF;
}

.chart-icon.green {
  color: #059669;

  background: #ECFDF5;
}

.chart-icon.purple {
  color: #7C3AED;

  background: #F5F3FF;
}

.chart-icon.orange {
  color: #EA580C;

  background: #FFF7ED;
}

.chart-container {
  width: 100%;

  min-height: 300px;
}

.donut-container {
  min-height: 300px;
}

.radial-container {
  min-height: 260px;
}


/* =========================================================
   COMPLETION
========================================================= */

.completion-summary {
  display: grid;

  grid-template-columns:
    repeat(2, 1fr);

  gap: 10px;

  margin-top: -5px;
}

.completion-summary div {
  padding: 9px;

  text-align: center;

  border-radius: 9px;

  background: #F8FAFC;
}

.completion-summary strong {
  display: block;

  color: #0F172A;

  font-size: 15px;

  font-weight: 800;
}

.completion-summary span {
  display: block;

  margin-top: 2px;

  color: #94A3B8;

  font-size: 8px;
}


/* =========================================================
   CONTENT
========================================================= */

.content-card {
  margin-bottom: 16px;
}

.content-grid {
  display: grid;

  grid-template-columns:
    repeat(6, minmax(0, 1fr));

  gap: 10px;

  margin-top: 14px;
}

.content-item {
  display: flex;

  align-items: center;

  gap: 9px;

  padding: 12px;

  border:
    1px solid #EEF2F7;

  border-radius: 11px;

  background: #FAFBFC;
}

.content-icon {
  width: 34px;
  height: 34px;

  min-width: 34px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 9px;

  font-size: 16px;
}

.content-icon.blue {
  color: #2563EB;

  background: #EFF6FF;
}

.content-icon.purple {
  color: #7C3AED;

  background: #F5F3FF;
}

.content-icon.green {
  color: #059669;

  background: #ECFDF5;
}

.content-icon.orange {
  color: #EA580C;

  background: #FFF7ED;
}

.content-icon.red {
  color: #E11D48;

  background: #FFF1F2;
}

.content-icon.cyan {
  color: #0891B2;

  background: #ECFEFF;
}

.content-item span {
  display: block;

  color: #64748B;

  font-size: 8px;

  font-weight: 700;
}

.content-item strong {
  display: block;

  margin-top: 2px;

  color: #0F172A;

  font-size: 16px;

  font-weight: 800;
}


/* =========================================================
   BADGE
========================================================= */

.chart-badge {
  padding: 5px 8px;

  border-radius: 7px;

  background: #F8FAFC;

  color: #64748B;

  font-size: 8px;

  font-weight: 700;
}


/* =========================================================
   RATING
========================================================= */

.rating-box {
  display: flex;

  align-items: center;

  gap: 4px;

  padding: 6px 9px;

  border-radius: 8px;

  background: #FFFBEB;

  color: #D97706;
}

.rating-box i {
  font-size: 14px;
}

.rating-box strong {
  font-size: 12px;
}


/* =========================================================
   REPORT
========================================================= */

.report-grid {
  display: grid;

  grid-template-columns:
    repeat(3, minmax(0, 1fr));

  gap: 16px;

  margin-bottom: 16px;
}

.report-card {
  min-height: 150px;
}

.report-top {
  display: flex;

  align-items: center;

  gap: 10px;
}

.report-icon {
  width: 39px;
  height: 39px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 10px;

  font-size: 18px;
}

.report-icon.yellow {
  color: #D97706;

  background: #FFFBEB;
}

.report-icon.purple {
  color: #7C3AED;

  background: #F5F3FF;
}

.report-icon.cyan {
  color: #0891B2;

  background: #ECFEFF;
}

.report-top h3 {
  margin: 0;

  color: #0F172A;

  font-size: 14px;

  font-weight: 800;
}

.report-top p {
  margin: 3px 0 0;

  color: #94A3B8;

  font-size: 9px;
}

.report-number {
  display: block;

  margin-top: 22px;

  color: #0F172A;

  font-size: 28px;

  line-height: 1;

  font-weight: 800;
}

.report-footer {
  display: flex;

  align-items: center;

  justify-content: space-between;

  margin-top: 13px;

  color: #94A3B8;

  font-size: 9px;
}

.report-footer i {
  color: #2563EB;

  font-size: 15px;
}


/* =========================================================
   ACTIVITY
========================================================= */

.activity-card {
  margin-bottom: 20px;
}

.activity-list {
  display: grid;
}

.activity-row {
  display: grid;

  grid-template-columns:
    32px minmax(0, 1fr) 18px;

  align-items: center;

  gap: 10px;

  min-height: 50px;

  border-bottom:
    1px solid #F1F5F9;
}

.activity-row:last-child {
  border-bottom: 0;
}

.activity-icon {
  width: 30px;
  height: 30px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 9px;

  background: #EFF6FF;

  color: #2563EB;

  font-size: 14px;
}

.activity-content {
  min-width: 0;
}

.activity-content strong {
  display: block;

  color: #334155;

  font-size: 11px;

  font-weight: 700;

  white-space: nowrap;

  overflow: hidden;

  text-overflow: ellipsis;
}

.activity-content span {
  display: block;

  margin-top: 3px;

  color: #94A3B8;

  font-size: 9px;
}

.activity-row > i {
  color: #CBD5E1;

  font-size: 16px;
}


/* =========================================================
   EMPTY
========================================================= */

.chart-empty {
  min-height: 230px;

  display: flex;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  gap: 9px;

  color: #94A3B8;

  font-size: 10px;
}

.chart-empty-icon {
  width: 42px;
  height: 42px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 12px;

  background: #F8FAFC;

  color: #CBD5E1;

  font-size: 20px;
}

.empty-state {
  min-height: 140px;

  display: flex;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  gap: 7px;

  color: #94A3B8;

  font-size: 10px;
}

.empty-state i {
  font-size: 27px;

  color: #CBD5E1;
}


/* =========================================================
   RESPONSIVE
========================================================= */

@media (max-width: 1200px) {

  .primary-stats,
  .secondary-stats {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  }

  .content-grid {
    grid-template-columns:
      repeat(3, minmax(0, 1fr));
  }

  .report-grid {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  }

}


@media (max-width: 1000px) {

  .charts-grid,
  .revenue-row {
    grid-template-columns: 1fr;
  }

}


@media (max-width: 768px) {

  .dashboard-wrapper {
    padding: 18px;
  }

  .dashboard-header {
    align-items: flex-start;

    flex-direction: column;
  }

  .header-actions {
    width: 100%;
  }

  .period-wrapper {
    flex: 1;
  }

  .period-select {
    width: 100%;
  }

  .primary-stats,
  .secondary-stats {
    grid-template-columns: 1fr;
  }

  .content-grid {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  }

  .report-grid {
    grid-template-columns: 1fr;
  }

}


@media (max-width: 480px) {

  .dashboard-wrapper {
    padding: 13px;
  }

  .dashboard-header h2 {
    font-size: 23px;
  }

  .header-actions {
    flex-direction: column;
  }

  .period-wrapper,
  .refresh-btn {
    width: 100%;
  }

  .content-grid {
    grid-template-columns: 1fr;
  }

  .dashboard-card {
    padding: 16px;
  }

  .chart-container {
    min-height: 270px;
  }

}

</style>
